import React from "react";
import { Link } from "react-router-dom";
import { Phone, Mail, MapPin, MessageCircle, ArrowRight } from "lucide-react";
import { useApp } from "@/context/AppContext";
import { useSeo } from "@/components/Seo";
import { getWhatsappNumbers } from "@/lib/whatsappLink";

export default function Contact() {
  useSeo({
    title: "Contact Tezz Khana Catering Service, Mumbai",
    description: "Get in touch with Tezz Khana Catering Service in Mumbai — reach us on WhatsApp or send an enquiry for your wedding, corporate or family event.",
    path: "/contact",
  });
  const { t, settings } = useApp();
  const waNumbers = getWhatsappNumbers(settings);

  return (
    <div className="container-x py-16">
      <div className="max-w-2xl">
        <span className="kbd-eyebrow">{t("contact.title")}</span>
        <h1 className="mt-4 text-4xl font-semibold text-secondary sm:text-5xl">
          Contact Tezz Khana Catering, Mumbai
        </h1>
        <p className="mt-3 text-sm text-muted-foreground">{t("contact.subtitle")}</p>
      </div>

      <div className="mt-12 grid gap-6 md:grid-cols-3">
        <Item
          icon={Phone}
          label={t("contact.phone")}
          value={
            settings?.contact_phone ? (
              <a href={`tel:${String(settings.contact_phone).replace(/[^\d+]/g, "")}`} data-testid="contact-phone-link">
                {settings.contact_phone}
              </a>
            ) : null
          }
          testid="contact-phone"
        />
        <Item icon={Mail} label={t("contact.email")} value={settings?.contact_email} testid="contact-email" />
        <Item icon={MapPin} label={t("contact.address")} value={settings?.address} testid="contact-address" />
      </div>

      <div className="mt-10 surface-card">
        {waNumbers.length > 0 ? (
          <div className="space-y-3">
            <div className="text-[11px] font-semibold uppercase tracking-[0.2em] text-accent">WhatsApp</div>
            {waNumbers.map((wa, idx) => {
              const digits = String(wa).replace(/[^\d]/g, "");
              return (
                <div
                  key={digits + idx}
                  data-testid={`contact-whatsapp-row-${idx + 1}`}
                  className="flex flex-col items-start justify-between gap-3 sm:flex-row sm:items-center"
                >
                  <div className="font-serif-display text-lg font-semibold text-secondary">
                    {waNumbers.length > 1 ? `WhatsApp ${idx + 1}: ` : ""}{wa}
                  </div>
                  <a
                    href={`https://wa.me/${digits}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid={idx === 0 && waNumbers.length === 1 ? "contact-whatsapp-link" : `contact-whatsapp-link-${idx + 1}`}
                    className="btn-primary"
                  >
                    <MessageCircle className="h-4 w-4" /> Chat on WhatsApp{waNumbers.length > 1 ? ` ${idx + 1}` : ""}
                  </a>
                </div>
              );
            })}
          </div>
        ) : (
          <p data-testid="contact-whatsapp-hint" className="text-sm text-muted-foreground">
            {t("contact.whatsapp_hint")}
          </p>
        )}
      </div>

      <div className="mt-12">
        <Link to="/quote" data-testid="contact-quote-cta" className="btn-primary">
          {t("hero.request_quote")} <ArrowRight className="h-4 w-4" />
        </Link>
      </div>
    </div>
  );
}

function Item({ icon: Icon, label, value, testid }) {
  return (
    <div className="surface-card" data-testid={testid}>
      <div className="grid h-10 w-10 place-items-center rounded-xl bg-primary/10 text-primary">
        <Icon className="h-4 w-4" />
      </div>
      <div className="mt-4 text-[11px] font-semibold uppercase tracking-[0.2em] text-muted-foreground">
        {label}
      </div>
      <div className="mt-1 font-serif-display text-lg font-semibold text-secondary">
        {value || "To be configured"}
      </div>
    </div>
  );
}
